import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEPARTMENT_DEFS,
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_MAP,
  JOB_OFFER_STATUS_MAP,
  draftOfferBody,
  jobOfferUrl,
  type EmploymentType,
  type JobOfferStatus,
} from "@goyone/shared";
import { Badge, Modal, Notice } from "../../components/ui";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/Toast";
import { api } from "../../lib/api";
import { date, dateTime, money, num } from "../../lib/format";
import { useAuth, useSession } from "../../auth/AuthProvider";
import type { OfferBasis } from "./Pipeline";

export interface OfferRow {
  id: string;
  number: string;
  title: string;
  deptKey: string | null;
  department: string | null;
  employmentType: string;
  employmentLabel: string;
  startDate: string | null;
  baseSalary: number;
  currency: string;
  probationMonths: number;
  benefits: string | null;
  body: string;
  status: JobOfferStatus;
  statusLabel: string;
  statusKind: string;
  statusNote: string;
  answerable: boolean;
  live: boolean;
  token: string;
  expiresOn: string | null;
  sentAt: string | null;
  decidedAt: string | null;
  acceptedName: string | null;
  declineReason: string | null;
  employeeId: string | null;
  applicationId: string;
  candidateName: string;
  job: { id: string; title: string };
}

interface EmployeeLite {
  id: string;
  name: string;
}

/* ==========================================================================
   قسم العرض داخل ملفّ الطلب
   ========================================================================== */

/**
 * عرضُ العمل كما يُدار.
 *
 * مسوّدةٌ تُراجَع وتُعدَّل، ثمّ رابطٌ يُفتح للمرشّح، ثمّ قبولٌ مؤرَّخٌ باسمه.
 * وما خرج لا يُعدَّل — من أراد التغيير يسحب ويكتب غيرَه، فيبقى الأوّل في
 * التاريخ ولا يرى المرشّح على الرابط نفسِه رقمًا غير الذي قرأه.
 */
export function OfferSection({
  applicationId,
  candidateName,
  jobTitle,
  basis,
  canAct,
}: {
  applicationId: string;
  candidateName: string;
  jobTitle: string;
  basis: OfferBasis;
  canAct: boolean;
}) {
  const { atLeast } = useAuth();
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<OfferRow | null>(null);
  const [hiring, setHiring] = useState<OfferRow | null>(null);

  const list = useQuery({
    queryKey: ["recruit", "offers", applicationId],
    queryFn: () =>
      api<OfferRow[]>(`/hr/recruit/applications/${applicationId}/offers`),
  });

  const rows = list.data ?? [];
  const liveOffer = rows.find((o) => o.live);

  return (
    <div className="mt-4">
      <div className="row" style={{ gap: "var(--sp-2)" }}>
        <h3 className="card-title" style={{ flex: 1 }}>
          عرض العمل
        </h3>
        {canAct && !liveOffer && (
          <button className="btn btn-sm" onClick={() => setWriting(true)}>
            <Icon name="send" size={14} />
            اكتب عرضًا
          </button>
        )}
      </div>

      {rows.length === 0 && (
        <p className="tiny mute">
          لا عرض بعد. يُكتب مسوّدةً على ورق الشركة، ويُراجَع، ثمّ يُفتح له رابطٌ
          يقبله المرشّح باسمه.
        </p>
      )}

      {rows.map((o) => (
        <OfferCard
          key={o.id}
          offer={o}
          canAct={canAct}
          canHire={atLeast("ADMIN")}
          onEdit={() => setEditing(o)}
          onHire={() => setHiring(o)}
        />
      ))}

      {(writing || editing) && (
        <OfferDialog
          applicationId={applicationId}
          offer={editing}
          candidateName={candidateName}
          jobTitle={jobTitle}
          basis={basis}
          onClose={() => {
            setWriting(false);
            setEditing(null);
          }}
        />
      )}

      {hiring && <HireDialog offer={hiring} onClose={() => setHiring(null)} />}
    </div>
  );
}

function OfferCard({
  offer,
  canAct,
  canHire,
  onEdit,
  onHire,
}: {
  offer: OfferRow;
  canAct: boolean;
  canHire: boolean;
  onEdit: () => void;
  onHire: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const url = jobOfferUrl(window.location.origin, offer.token);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ["recruit"] });

  const send = useMutation({
    mutationFn: () =>
      api(`/hr/recruit/offers/${offer.id}/send`, { method: "POST" }),
    onSuccess: () => {
      refresh();
      toast("فُتح الرابط — أرسله إلى المرشّح");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الفتح"),
  });

  const withdraw = useMutation({
    mutationFn: () =>
      api(`/hr/recruit/offers/${offer.id}/withdraw`, {
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      refresh();
      toast("سُحب العرض");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر السحب"),
  });

  return (
    <div className="rc-offer">
      <div className="row" style={{ gap: "var(--sp-2)" }}>
        <strong className="num">{offer.number}</strong>
        <Badge kind={offer.statusKind}>{offer.statusLabel}</Badge>
        <span className="tiny mute" style={{ flex: 1 }}>
          {offer.statusNote}
        </span>
      </div>

      <div className="rc-offer-facts">
        <span>{offer.title}</span>
        <span className="num">{money(offer.baseSalary, offer.currency)}</span>
        <span>{offer.employmentLabel}</span>
        <span className="num">مباشرة {date(offer.startDate)}</span>
        {offer.expiresOn && (
          <span className="num">الردّ حتى {date(offer.expiresOn)}</span>
        )}
      </div>

      {offer.status === "ACCEPTED" && (
        <Notice kind="success" title="قَبِل العرض" icon="check">
          وقّع باسم «{offer.acceptedName}» في {dateTime(offer.decidedAt)}.
          {offer.employeeId
            ? " وفُتح له ملفٌّ وظيفيّ."
            : " ولم يُفتح له ملفٌّ وظيفيٌّ بعد."}
        </Notice>
      )}

      {offer.status === "DECLINED" && offer.declineReason && (
        <Notice kind="danger" title="رفض العرض" icon="x">
          {offer.declineReason}
        </Notice>
      )}

      {offer.status !== "DRAFT" && (
        <div className="rc-link">
          <code dir="ltr">{url}</code>
          <button
            className="btn btn-sm"
            onClick={() => {
              void navigator.clipboard.writeText(url);
              toast("نُسخ الرابط");
            }}
          >
            <Icon name="paperclip" size={14} />
            نسخ
          </button>
        </div>
      )}

      <div className="row mt-2" style={{ gap: "var(--sp-2)" }}>
        <a
          className="btn btn-sm"
          href={`/print/offer/${offer.id}`}
          target="_blank"
          rel="noreferrer"
        >
          <Icon name="printer" size={14} />
          اطبع على ورق الشركة
        </a>

        {canAct && offer.status === "DRAFT" && (
          <>
            <button className="btn btn-sm" onClick={onEdit}>
              <Icon name="edit" size={14} />
              راجع وعدّل
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => send.mutate()}
              disabled={send.isPending}
            >
              افتح الرابط للمرشّح
            </button>
          </>
        )}

        {canAct && offer.live && offer.status !== "ACCEPTED" && (
          <button
            className="btn btn-sm"
            onClick={() => withdraw.mutate()}
            disabled={withdraw.isPending}
          >
            اسحب
          </button>
        )}

        {canHire && offer.status === "ACCEPTED" && !offer.employeeId && (
          <button className="btn btn-sm btn-primary" onClick={onHire}>
            <Icon name="user" size={14} />
            افتح الملفّ الوظيفيّ
          </button>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   كتابة العرض
   ========================================================================== */

function OfferDialog({
  applicationId,
  offer,
  candidateName,
  jobTitle,
  basis,
  onClose,
}: {
  applicationId: string;
  offer: OfferRow | null;
  candidateName: string;
  jobTitle: string;
  basis: OfferBasis;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  /*
   * العرضُ يُفتح على ما اعتُمد في ملفّ الوظيفة.
   *
   * القسمُ ونوعُ التعاقد وأوّلُ النطاق كُتبت هناك مرّةً، وكتابتُها هنا
   * بيدٍ ثانيةٍ تُخرج عرضًا يخالف المعتمَد بلا أن ينتبه أحد. وهي قيمٌ
   * أوّليّةٌ تُعدَّل: الحدُّ اقتراحٌ لا سقف.
   */
  const [title, setTitle] = useState(offer?.title ?? jobTitle);
  const [dept, setDept] = useState(offer?.deptKey ?? basis.deptKey ?? "");
  const [employmentType, setEmploymentType] = useState<EmploymentType>(
    (offer?.employmentType as EmploymentType) ??
      (basis.employmentType as EmploymentType),
  );
  const [startDate, setStartDate] = useState(
    offer?.startDate ??
      new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10),
  );
  const [salary, setSalary] = useState(
    String(offer?.baseSalary ?? basis.salaryMin ?? ""),
  );
  const [probation, setProbation] = useState(
    String(offer?.probationMonths ?? 3),
  );
  const [benefits, setBenefits] = useState(offer?.benefits ?? "");
  const [body, setBody] = useState(offer?.body ?? "");
  const [expiresOn, setExpiresOn] = useState(offer?.expiresOn ?? "");

  const asked = salary === "" ? null : Number(salary);
  const outsideRange =
    asked !== null &&
    ((basis.salaryMin !== null && asked < basis.salaryMin) ||
      (basis.salaryMax !== null && asked > basis.salaryMax));

  /* رمزُ العملة كما يُكتب في مستندات الشركة — لا رمزُها في العمود. */
  const profile = useQuery({
    queryKey: ["settings"],
    queryFn: () => api<{ settings: { currencyLabel: string } }>("/settings"),
  });

  const currency = offer?.currency ?? "EGP";
  const currencyLabel = profile.data?.settings.currencyLabel || currency;
  const valid =
    title.trim().length >= 2 && startDate !== "" && Number(salary) > 0;

  /*
   * الصياغةُ تُقترح ولا تُفرض.
   *
   * تُبنى مرّةً بضغطةٍ ثمّ تُحرَّر — وتوليدٌ تلقائيٌّ عند كلّ تغييرٍ يمحو
   * ما كتبه من راجع.
   */
  const suggest = () =>
    setBody(
      draftOfferBody({
        candidateName,
        companyName: session.tenant.name,
        title: title.trim(),
        department: dept
          ? (DEPARTMENT_DEFS.find((d) => d.key === dept)?.label ?? null)
          : null,
        employmentLabel: EMPLOYMENT_TYPE_MAP[employmentType].label,
        /* بلغة الخطاب لا بلغة العمود — كما يصوغه الخادم سواء. */
        startDate: date(startDate),
        salaryText: `${Number(salary).toLocaleString("ar-EG")} ${currencyLabel}`,
        probationMonths: Number(probation) || 0,
        benefits: benefits.trim() || null,
      }),
    );

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title: title.trim(),
        deptKey: dept || null,
        employmentType,
        startDate,
        baseSalary: Number(salary),
        probationMonths: Number(probation) || 0,
        benefits: benefits.trim() || null,
        body: body.trim() || null,
        expiresOn: expiresOn || null,
      };
      return offer
        ? api(`/hr/recruit/offers/${offer.id}`, {
            method: "PATCH",
            body: payload,
          })
        : api(`/hr/recruit/applications/${applicationId}/offers`, {
            method: "POST",
            body: payload,
          });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
      toast(offer ? "حُفظ العرض" : "كُتب العرض مسوّدةً");
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحفظ"),
  });

  return (
    <Modal
      title={offer ? `مراجعة ${offer.number}` : "اكتب عرض عمل"}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "جارٍ الحفظ" : "احفظ مسوّدةً"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="of-title">
            المسمّى الوظيفي في العرض
          </label>
          <input
            id="of-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <span className="tiny mute">
            يُجمَّد في العرض — تعديلُ الوظيفة بعده لا يغيّر ما قَبِله المرشّح.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="of-dept">
            القسم
          </label>
          <select
            id="of-dept"
            className="select"
            value={dept}
            onChange={(e) => setDept(e.target.value)}
          >
            <option value="">بلا قسم</option>
            {DEPARTMENT_DEFS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="of-type">
            نوع التعاقد
          </label>
          <select
            id="of-type"
            className="select"
            value={employmentType}
            onChange={(e) =>
              setEmploymentType(e.target.value as EmploymentType)
            }
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {EMPLOYMENT_TYPE_MAP[t].label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label req" htmlFor="of-salary">
            الراتب الأساسي
          </label>
          <input
            id="of-salary"
            className="input num"
            type="number"
            min={1}
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
          />
          {/*
            والنطاقُ يُقال ولا يُفرض: من يوقّع على رقمٍ خارجه يعرف أنّه
            خارجه، والاستثناءُ قرارُ من يملكه لا خطأٌ يُردّ في نموذج.
          */}
          {(basis.salaryMin !== null || basis.salaryMax !== null) && (
            <span
              className={outsideRange ? "tiny" : "tiny mute"}
              style={outsideRange ? { color: "var(--warning)" } : undefined}
            >
              النطاق المعتمَد في ملفّ الوظيفة:{" "}
              {basis.salaryMin !== null ? num(basis.salaryMin) : "—"} —{" "}
              {basis.salaryMax !== null ? num(basis.salaryMax) : "—"}
              {outsideRange ? " · وهذا الرقم خارجه" : ""}
            </span>
          )}
        </div>

        <div className="field">
          <label className="label req" htmlFor="of-start">
            تاريخ المباشرة
          </label>
          <input
            id="of-start"
            className="input num"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="of-prob">
            فترة الاختبار بالأشهر
          </label>
          <input
            id="of-prob"
            className="input num"
            type="number"
            min={0}
            max={12}
            value={probation}
            onChange={(e) => setProbation(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="of-exp">
            مهلة الردّ
          </label>
          <input
            id="of-exp"
            className="input num"
            type="date"
            value={expiresOn}
            onChange={(e) => setExpiresOn(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="of-benefits">
            المزايا
          </label>
          <textarea
            id="of-benefits"
            className="input"
            rows={3}
            value={benefits}
            onChange={(e) => setBenefits(e.target.value)}
            placeholder="تأمين طبّي، بدل انتقالات، إجازة سنوية ٢١ يومًا…"
          />
        </div>
      </div>

      <div className="row mt-4" style={{ gap: "var(--sp-2)" }}>
        <h3 className="card-title" style={{ flex: 1 }}>
          نصّ الخطاب
        </h3>
        <button className="btn btn-sm" type="button" onClick={suggest}>
          <Icon name="refresh" size={14} />
          {body ? "أعِد الصياغة" : "اصغ من الحقول"}
        </button>
      </div>

      <p className="tiny mute">
        يُطبع على ورق الشركة ويُقرأ على الرابط كما هو. وإعادةُ الصياغة تمحو ما
        حرّرتَه.
      </p>

      <textarea
        className="input mt-2"
        rows={12}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="اتركه فارغًا فيُصاغ من الحقول عند الحفظ."
      />
    </Modal>
  );
}

/* ==========================================================================
   فتح الملفّ الوظيفيّ
   ========================================================================== */

/**
 * ما لا يعرفه العرضُ يُسأل هنا.
 *
 * الراتبُ والمسمّى وتاريخُ المباشرة تُنسخ من العرض لا تُكتب ثانيةً —
 * راتبٌ يُكتب مرّتين يختلف مرّةً. والرقمُ القوميّ والمدير المباشر لا
 * يعرفهما العرضُ، ولا يُخمَّنان: ملفٌّ بأرقامٍ مخمَّنةٍ يُبنى عليه مرتّبٌ
 * وتأمين.
 */
function HireDialog({
  offer,
  onClose,
}: {
  offer: OfferRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const employees = useQuery({
    queryKey: ["employees", "managers"],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        "/hr/employees?pageSize=200&status=ACTIVE",
      ),
  });

  const [code, setCode] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [balance, setBalance] = useState("21");
  const [hireDate, setHireDate] = useState(offer.startDate ?? "");

  const hire = useMutation({
    mutationFn: () =>
      api<{ employeeId: string; code: string }>(
        `/hr/recruit/offers/${offer.id}/hire`,
        {
          method: "POST",
          body: {
            code: code.trim() || null,
            nationalId: nationalId.trim() || null,
            managerId: managerId || null,
            annualLeaveBalance: Number(balance) || 21,
            hireDate: hireDate || undefined,
          },
        },
      ),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
      toast(`فُتح الملفّ ${r.code}`);
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الفتح"),
  });

  return (
    <Modal
      title={`الملفّ الوظيفيّ لـ${offer.candidateName}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => hire.mutate()}
            disabled={hire.isPending}
          >
            {hire.isPending ? "جارٍ الفتح" : "افتح الملفّ"}
          </button>
        </>
      }
    >
      <Notice title="ما يُنسخ من العرض" icon="check">
        {offer.title} · {money(offer.baseSalary, offer.currency)} ·{" "}
        {offer.employmentLabel} · مباشرة {date(offer.startDate)}
      </Notice>

      <div className="form-grid mt-4">
        <div className="field">
          <label className="label" htmlFor="hi-code">
            كود الموظّف
          </label>
          <input
            id="hi-code"
            className="input num"
            dir="ltr"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="يُولَّد تلقائيًّا إن تُرك"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="hi-nid">
            الرقم القومي
          </label>
          <input
            id="hi-nid"
            className="input num"
            dir="ltr"
            value={nationalId}
            onChange={(e) => setNationalId(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="hi-mgr">
            المدير المباشر
          </label>
          <select
            id="hi-mgr"
            className="select"
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
          >
            <option value="">بلا مدير مباشر</option>
            {(employees.data?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="hi-bal">
            رصيد الإجازة السنوية
          </label>
          <input
            id="hi-bal"
            className="input num"
            type="number"
            min={0}
            max={365}
            value={balance}
            onChange={(e) => setBalance(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="hi-date">
            تاريخ التعيين
          </label>
          <input
            id="hi-date"
            className="input num"
            type="date"
            value={hireDate}
            onChange={(e) => setHireDate(e.target.value)}
          />
          <span className="tiny mute">
            الافتراضيّ تاريخُ المباشرة في العرض.
          </span>
        </div>
      </div>

      <p className="tiny mute mt-4">
        القسمُ يُنسخ من العرض ويصير مركزَ التكلفة. وما بقي — التأمينات وضريبة
        الدخل والأوراق — يُستكمل من ملفّ الموظّف بعد فتحه.
      </p>
    </Modal>
  );
}

export { JOB_OFFER_STATUS_MAP };
